// The collection and cosmetics catalogue: every Pal, mask, colour, reveal effect, Inspector hat and Popper ray.
// Art keys (`art`) name files in client/assets/ui/ (painted kit) — the catalogue is the single source the Burrow,
// the shop, the results card and the server's cosmetic validation all read.

export type Rarity = "common" | "rare" | "epic" | "legendary";
export const RARITY_ORDER: Rarity[] = ["common", "rare", "epic", "legendary"];
export const RARITY_LABEL: Record<Rarity, string> = { common: "COMMON", rare: "RARE", epic: "EPIC", legendary: "LEGENDARY" };

export interface Pal { id: string; name: string; rarity: Rarity; blurb: string; }
// 24 at launch: 12 common · 7 rare · 4 epic · 1 legendary (docs/DESIGN.md §5)
export const PALS: Pal[] = [
  { id: "pip", name: "Pip", rarity: "common", blurb: "A mouse who collects bottle caps." },
  { id: "bun", name: "Bun", rarity: "common", blurb: "Naps in the softest capsule." },
  { id: "moss", name: "Moss", rarity: "common", blurb: "A snail. Fastest in the Burrow, he says." },
  { id: "toot", name: "Toot", rarity: "common", blurb: "A pigeon who knows every rooftop." },
  { id: "nib", name: "Nib", rarity: "common", blurb: "A beetle with a polished shell." },
  { id: "puddle", name: "Puddle", rarity: "common", blurb: "A frog who loves rainy rounds." },
  { id: "crumb", name: "Crumb", rarity: "common", blurb: "A sparrow who follows the bakery cart." },
  { id: "wiggle", name: "Wiggle", rarity: "common", blurb: "A worm with big plans." },
  { id: "button", name: "Button", rarity: "common", blurb: "A hamster who hoards coins in his cheeks." },
  { id: "pebble", name: "Pebble", rarity: "common", blurb: "A mole who dug the first drain." },
  { id: "fizz", name: "Fizz", rarity: "common", blurb: "A bumblebee who hums the parade song." },
  { id: "sprout", name: "Sprout", rarity: "common", blurb: "A tiny turtle with a flowerpot shell." },
  { id: "bandit_jr", name: "Bandit Jr.", rarity: "rare", blurb: "A raccoon kit practising his first heist." },
  { id: "tumble", name: "Tumble", rarity: "rare", blurb: "A hedgehog who rolls everywhere." },
  { id: "lantern", name: "Lantern", rarity: "rare", blurb: "A firefly who lights the Burrow at night." },
  { id: "mopsy", name: "Mopsy", rarity: "rare", blurb: "A sheepdog pup who herds the capsules." },
  { id: "rascal", name: "Rascal", rarity: "rare", blurb: "A ferret who can fit through any drain." },
  { id: "swirl", name: "Swirl", rarity: "rare", blurb: "A chameleon. Best disguises in town." },
  { id: "biscuit", name: "Biscuit", rarity: "rare", blurb: "A corgi who guards the Waiting shelf." },
  { id: "baron", name: "Baron Whiskers", rarity: "epic", blurb: "A cat with a monocle and opinions." },
  { id: "captain", name: "Captain Quack", rarity: "epic", blurb: "A duck who sailed the fountain." },
  { id: "duchess", name: "Duchess", rarity: "epic", blurb: "An opossum in pearls. Very proper." },
  { id: "gizmo", name: "Gizmo", rarity: "epic", blurb: "A wind-up mouse who never runs down." },
  { id: "midas", name: "Midas", rarity: "legendary", blurb: "The Golden Raccoon. Everything he touches shines." },
];
export const palById = (id: string) => PALS.find((p) => p.id === id);

export type CosKind = "mask" | "color" | "reveal" | "hat" | "ray";
export interface Cosmetic { id: string; kind: CosKind; name: string; price: number; level: number; }
// price 0 + level 1 = owned from the start; level > 1 = free unlock at that level; price > 0 = buy with coins
export const COSMETICS: Cosmetic[] = [
  // Sneak true-self: what pops out when you are caught
  { id: "mask_classic", kind: "mask", name: "Classic Bandit", price: 0, level: 1 },
  { id: "mask_star", kind: "mask", name: "Starry", price: 0, level: 4 },
  { id: "mask_fox", kind: "mask", name: "Fox", price: 600, level: 1 },
  { id: "mask_owl", kind: "mask", name: "Night Owl", price: 900, level: 1 },
  { id: "mask_bolt", kind: "mask", name: "Lightning", price: 0, level: 12 },
  { id: "mask_heart", kind: "mask", name: "Sweetheart", price: 1200, level: 1 },
  { id: "mask_tiger", kind: "mask", name: "Tiger", price: 2500, level: 1 },
  { id: "mask_gold", kind: "mask", name: "Golden Bandit", price: 0, level: 30 },
  { id: "col_plum", kind: "color", name: "Plum", price: 0, level: 1 },
  { id: "col_mint", kind: "color", name: "Mint", price: 0, level: 2 },
  { id: "col_tangerine", kind: "color", name: "Tangerine", price: 300, level: 1 },
  { id: "col_sky", kind: "color", name: "Sky", price: 300, level: 1 },
  { id: "col_cherry", kind: "color", name: "Cherry", price: 500, level: 1 },
  { id: "col_lemon", kind: "color", name: "Lemon", price: 0, level: 8 },
  { id: "col_night", kind: "color", name: "Midnight", price: 1500, level: 1 },
  { id: "col_rainbow", kind: "color", name: "Rainbow", price: 0, level: 40 },
  { id: "rev_confetti", kind: "reveal", name: "Confetti", price: 0, level: 1 },
  { id: "rev_feathers", kind: "reveal", name: "Feathers", price: 800, level: 1 },
  { id: "rev_bubbles", kind: "reveal", name: "Bubbles", price: 0, level: 6 },
  { id: "rev_leaves", kind: "reveal", name: "Autumn Leaves", price: 1000, level: 1 },
  { id: "rev_stars", kind: "reveal", name: "Stardust", price: 0, level: 18 },
  { id: "rev_coins", kind: "reveal", name: "Coin Shower", price: 4000, level: 1 },
  // the Inspector
  { id: "hat_deerstalker", kind: "hat", name: "Deerstalker", price: 0, level: 1 },
  { id: "hat_bowler", kind: "hat", name: "Bowler", price: 0, level: 3 },
  { id: "hat_tophat", kind: "hat", name: "Top Hat", price: 1200, level: 1 },
  { id: "hat_captain", kind: "hat", name: "Captain's Cap", price: 0, level: 15 },
  { id: "hat_fedora", kind: "hat", name: "Fedora", price: 2000, level: 1 },
  { id: "hat_crown", kind: "hat", name: "Crown", price: 0, level: 35 },
  { id: "ray_confetti", kind: "ray", name: "Confetti Ray", price: 0, level: 1 },
  { id: "ray_bubble", kind: "ray", name: "Bubble Ray", price: 700, level: 1 },
  { id: "ray_spark", kind: "ray", name: "Sparkler", price: 0, level: 10 },
  { id: "ray_rainbow", kind: "ray", name: "Rainbow Ray", price: 2500, level: 1 },
  { id: "ray_snow", kind: "ray", name: "Snowflake Ray", price: 1500, level: 1 },
  { id: "ray_gold", kind: "ray", name: "Golden Ray", price: 0, level: 25 },
];
export const cosById = (id: string) => COSMETICS.find((c) => c.id === id);
export const DEFAULT_EQUIP: Record<CosKind, string> = { mask: "mask_classic", color: "col_plum", reveal: "rev_confetti", hat: "hat_deerstalker", ray: "ray_confetti" };

// Daily orders: the pool (docs/DESIGN.md §5). `stat` = the round-result counter that advances it.
export interface OrderDef { id: string; text: string; stat: "stash" | "perfect" | "catch" | "win" | "clean" | "ignored"; goal: number; }
export const ORDER_POOL: OrderDef[] = [
  { id: "stash3", text: "Stash 3 capsules", stat: "stash", goal: 3 },
  { id: "stash6", text: "Stash 6 capsules", stat: "stash", goal: 6 },
  { id: "perfect5", text: "Nail 5 PERFECT calls", stat: "perfect", goal: 5 },
  { id: "perfect12", text: "Nail 12 PERFECT calls", stat: "perfect", goal: 12 },
  { id: "catch2", text: "Catch 2 Sneaks as Inspector", stat: "catch", goal: 2 },
  { id: "win2", text: "Win 2 rounds", stat: "win", goal: 2 },
  { id: "clean1", text: "Survive a full round unpopped", stat: "clean", goal: 1 },
  { id: "ignored2", text: "Ignore 2 fake whistles", stat: "ignored", goal: 2 },
];
